import { useId } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/atoms/Buttons';
import { Spinner } from '@/components/atoms/Spinner';
import { ModalComponent } from '@/components/molecules/ModalComponent';
import { ToastInfo } from '@/components/molecules/ToastTemplate/ToastTemplate';
import {
    FormTextarea,
    FormTextField,
    useServerErrorMessage,
} from '@/components/organisms/FishGroupForms';
import { useGetRevertPreview, useRevertFishGroupOperation } from '@/domain/fishGroups';
import { formatDateTimeWorded } from '@/utils/date';

import RevertPreviewSections from './RevertPreviewSections';

import actionStyles from '../actionModals.module.scss';
import styles from './RevertOperationModal.module.scss';

interface Props {
    clientId: number;
    operationId: number;
    isOpen: boolean;
    onClose: () => void;
}

interface FormValues {
    reason: string;
    notes: string;
}

const DEFAULT_VALUES: FormValues = { reason: '', notes: '' };
const REASON_MAX_LENGTH = 255;

/**
 * Annuls an operation recorded by mistake. Shows what the revert will do (and
 * what blocks it) before asking for a reason. Bioscopes are not moved.
 */
export default function RevertOperationModal(props: Props) {
    const { clientId, operationId, isOpen, onClose } = props;
    const { t } = useTranslation('fishGroups');
    const formId = `${useId()}-form`;
    const {
        handleSubmit,
        control,
        reset,
        formState: { isSubmitting },
    } = useForm<FormValues>({ defaultValues: DEFAULT_VALUES });

    const {
        data: preview,
        isLoading: isPreviewLoading,
        error: previewError,
    } = useGetRevertPreview(clientId, operationId, { enabled: isOpen });

    const resetAll = () => {
        reset(DEFAULT_VALUES);
        resetMutation();
    };

    const {
        revert,
        error: revertError,
        reset: resetMutation,
    } = useRevertFishGroupOperation(clientId, operationId, {
        onSuccess: () => {
            ToastInfo({
                content: t('actions.revert.successToast'),
                status: 'success',
                icon: 'success',
                options: { toastId: 'fish-group-revert-success', autoClose: 5000 },
            });
            resetAll();
            onClose();
        },
    });

    const previewErrorMessage = useServerErrorMessage(previewError);
    const revertErrorMessage = useServerErrorMessage(revertError);

    const handleClose = () => {
        if (isSubmitting) return;
        resetAll();
        onClose();
    };

    const onSubmit = async (values: FormValues) => {
        await revert({
            reason: values.reason.trim(),
            ...(values.notes.trim() ? { notes: values.notes.trim() } : {}),
        }).catch(() => {});
    };

    const canSubmit = preview?.revertible === true && !isSubmitting;

    const body = (
        <div className={actionStyles.form}>
            <p className={styles.intro}>{t('actions.revert.intro')}</p>
            {isPreviewLoading && <Spinner size="lg" />}
            {previewErrorMessage && (
                <p className={actionStyles.serverError}>{previewErrorMessage}</p>
            )}
            {preview && (
                <>
                    <p className={actionStyles.contextLine}>
                        {t('actions.revert.context')}{' '}
                        <span className={actionStyles.contextStrong}>
                            {t(`actions.revert.operationTypes.${preview.operationType}`)}
                        </span>
                        <span className={actionStyles.contextMeta}>
                            {' · '}
                            {formatDateTimeWorded(preview.operationDate, 'minute')}
                        </span>
                    </p>
                    <RevertPreviewSections preview={preview} />
                    {preview.revertible && (
                        <>
                            <p className={styles.sectionDescription}>
                                {t('actions.revert.dataNote')}
                            </p>
                            <form
                                className={actionStyles.form}
                                onSubmit={handleSubmit(onSubmit)}
                                id={formId}>
                                <FormTextField
                                    control={control}
                                    name="reason"
                                    label={t('actions.revert.fields.reason')}
                                    placeholder={t('actions.revert.fields.reasonPlaceholder')}
                                    rules={{
                                        validate: (value: string) => value.trim().length > 0,
                                        // A message of its own: without one the field falls
                                        // back to the "required" copy, which misleads here.
                                        maxLength: {
                                            value: REASON_MAX_LENGTH,
                                            message: t('actions.revert.fields.reasonTooLong', {
                                                max: REASON_MAX_LENGTH,
                                            }),
                                        },
                                    }}
                                />
                                <FormTextarea
                                    control={control}
                                    name="notes"
                                    label={t('actions.fields.notes')}
                                    optional
                                />
                            </form>
                        </>
                    )}
                </>
            )}
            {revertErrorMessage && <p className={actionStyles.serverError}>{revertErrorMessage}</p>}
        </div>
    );

    const footer = (
        <>
            {isSubmitting && <Spinner />}
            <Button variant="secondary" onClick={handleClose} disabled={isSubmitting}>
                {t('actions.cancel')}
            </Button>
            <Button variant="danger" type="submit" form={formId} disabled={!canSubmit}>
                {t('actions.revert.submit')}
            </Button>
        </>
    );

    return (
        <ModalComponent
            isOpen={isOpen}
            onClose={handleClose}
            title={t('actions.revert.title')}
            body={body}
            footer={footer}
            backdrop="static"
        />
    );
}
